import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '@/App';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import {
  Save, Printer, FileText, ArrowLeft, Check,
  Bold, Italic, Underline, Strikethrough,
  Heading1, Heading2, Heading3, Pilcrow,
  List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight,
  Type, Highlighter, Undo2, Redo2,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExt from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';

const TEXT_COLORS = [
  '#000000', '#dc2626', '#ea580c', '#ca8a04', '#16a34a',
  '#2563eb', '#7c3aed', '#db2777', '#64748b', '#ffffff',
];
const HIGHLIGHT_COLORS = [
  '#fef08a', '#bbf7d0', '#bfdbfe', '#fecaca', '#e9d5ff',
  '#fed7aa', '#fbcfe8', '#e2e8f0', '#ffffff',
];

const NodeNotePage = () => {
  const [searchParams] = useSearchParams();
  const mapId = searchParams.get('mapId');
  const nodeId = searchParams.get('nodeId');

  const [nodeLabel, setNodeLabel] = useState('');
  const [mapName, setMapName] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialContent, setInitialContent] = useState('');
  const saveTimerRef = useRef(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const colorRef = useRef(null);
  const highlightRef = useRef(null);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      UnderlineExt,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none focus:outline-none min-h-[55vh] px-8 py-6',
        'data-testid': 'note-editor',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => doSave(html), 1500);
    },
  });

  // Load initial content into editor when ready
  useEffect(() => {
    if (editor && initialContent && !editor.isDestroyed) {
      editor.commands.setContent(initialContent);
    }
  }, [editor, initialContent]);

  // Fetch notes
  useEffect(() => {
    if (!mapId || !nodeId) return;
    const fetchNotes = async () => {
      try {
        const res = await axios.get(`${API}/mindmaps/${mapId}/nodes/${nodeId}/notes`, { headers: getAuthHeader() });
        setInitialContent(res.data.content || '');
        setNodeLabel(res.data.node_label || nodeId);
        setMapName(res.data.map_name || '');
      } catch {
        toast.error('Failed to load notes');
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [mapId, nodeId]);

  // Save function
  const doSave = useCallback(async (html) => {
    if (!mapId || !nodeId) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/mindmaps/${mapId}/nodes/${nodeId}/notes`,
        { content: html },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      setLastSaved(new Date());
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [mapId, nodeId]);

  // Manual save
  const handleSave = useCallback(() => {
    if (!editor) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    doSave(editor.getHTML());
  }, [editor, doSave]);

  // Print
  const handlePrint = useCallback(() => {
    if (!editor) return;
    const printWin = window.open('', '_blank');
    printWin.document.write(`
      <html>
      <head>
        <title>${nodeLabel} — Notes</title>
        <style>
          body { font-family: 'Segoe UI', system-ui, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 24px; color: #1e293b; line-height: 1.7; }
          h1 { color: #2F5496; font-size: 24px; border-bottom: 2px solid #dbeafe; padding-bottom: 8px; margin-bottom: 4px; }
          .map-name { color: #94a3b8; font-size: 13px; margin-bottom: 24px; }
          h2 { font-size: 20px; color: #334155; margin-top: 24px; }
          h3 { font-size: 16px; color: #475569; margin-top: 20px; }
          p { margin: 8px 0; }
          ul, ol { padding-left: 24px; margin: 8px 0; }
          li { margin: 4px 0; }
          mark { padding: 2px 4px; border-radius: 2px; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <h1>${nodeLabel}</h1>
        <div class="map-name">${mapName}</div>
        ${editor.getHTML()}
      </body>
      </html>
    `);
    printWin.document.close();
    printWin.print();
  }, [editor, nodeLabel, mapName]);

  // Keyboard shortcut: Ctrl+S
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  // Close color pickers on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colorRef.current && !colorRef.current.contains(e.target)) setColorOpen(false);
      if (highlightRef.current && !highlightRef.current.contains(e.target)) setHighlightOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!mapId || !nodeId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Invalid link — missing map or node ID</p>
        </div>
      </div>
    );
  }

  if (loading || !editor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading notes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="node-note-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.close()}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
              title="Close"
              data-testid="close-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-base font-bold text-slate-900 flex items-center gap-2 truncate" data-testid="note-title">
                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                {nodeLabel}
              </h1>
              <p className="text-xs text-slate-400 truncate">{mapName}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {lastSaved && (
              <span className="text-xs text-slate-400 flex items-center gap-1 mr-1">
                <Check className="w-3 h-3 text-green-500" />
                Saved
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors"
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>

        {/* Formatting Toolbar */}
        <Toolbar
          editor={editor}
          colorOpen={colorOpen}
          setColorOpen={setColorOpen}
          highlightOpen={highlightOpen}
          setHighlightOpen={setHighlightOpen}
          colorRef={colorRef}
          highlightRef={highlightRef}
        />
      </div>

      {/* Editor */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden editor-wrapper">
          <EditorContent editor={editor} />
        </div>
      </div>

      <Toaster position="top-right" />

      <style>{`
        .editor-wrapper .tiptap {
          line-height: 1.8;
          font-size: 15px;
          color: #334155;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }
        .editor-wrapper .tiptap:focus {
          outline: none;
        }
        .editor-wrapper .tiptap h1 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin: 16px 0 8px;
          line-height: 1.3;
        }
        .editor-wrapper .tiptap h2 {
          font-size: 22px;
          font-weight: 600;
          color: #334155;
          margin: 14px 0 6px;
          line-height: 1.3;
        }
        .editor-wrapper .tiptap h3 {
          font-size: 18px;
          font-weight: 600;
          color: #475569;
          margin: 12px 0 4px;
          line-height: 1.4;
        }
        .editor-wrapper .tiptap p {
          margin: 4px 0;
        }
        .editor-wrapper .tiptap ul {
          list-style-type: disc;
          padding-left: 24px;
          margin: 6px 0;
        }
        .editor-wrapper .tiptap ol {
          list-style-type: decimal;
          padding-left: 24px;
          margin: 6px 0;
        }
        .editor-wrapper .tiptap li {
          margin: 2px 0;
        }
        .editor-wrapper .tiptap li p {
          margin: 0;
        }
        .editor-wrapper .tiptap blockquote {
          border-left: 3px solid #cbd5e1;
          padding-left: 16px;
          margin: 8px 0;
          color: #64748b;
        }
        .editor-wrapper .tiptap mark {
          padding: 2px 4px;
          border-radius: 2px;
        }
        .editor-wrapper .tiptap hr {
          border: none;
          border-top: 2px solid #e2e8f0;
          margin: 16px 0;
        }
        .editor-wrapper .tiptap p.is-editor-empty:first-child::before {
          content: 'Start writing your notes...';
          color: #94a3b8;
          float: left;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

/* ── Toolbar ── */
const Toolbar = ({ editor, colorOpen, setColorOpen, highlightOpen, setHighlightOpen, colorRef, highlightRef }) => {
  if (!editor) return null;

  const TBtn = ({ onClick, active, title, children, testId }) => (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );

  const Sep = () => <div className="w-px h-5 bg-slate-200 mx-0.5" />;

  return (
    <div className="max-w-4xl mx-auto flex items-center gap-0.5 px-4 py-1.5 border-t border-slate-100 flex-wrap" data-testid="editor-toolbar">
      <TBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" testId="undo-btn">
        <Undo2 className="w-4 h-4" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" testId="redo-btn">
        <Redo2 className="w-4 h-4" />
      </TBtn>
      <Sep />
      <TBtn
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
        testId="bold-btn"
      >
        <Bold className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
        testId="italic-btn"
      >
        <Italic className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        title="Underline (Ctrl+U)"
        testId="underline-btn"
      >
        <Underline className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')}
        title="Strikethrough"
        testId="strike-btn"
      >
        <Strikethrough className="w-4 h-4" />
      </TBtn>
      <Sep />
      <TBtn
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive('heading', { level: 1 })}
        title="Heading 1"
        testId="h1-btn"
      >
        <Heading1 className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
        testId="h2-btn"
      >
        <Heading2 className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
        testId="h3-btn"
      >
        <Heading3 className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().setParagraph().run()}
        active={editor.isActive('paragraph') && !editor.isActive('heading')}
        title="Paragraph"
        testId="paragraph-btn"
      >
        <Pilcrow className="w-4 h-4" />
      </TBtn>
      <Sep />
      <TBtn
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet List"
        testId="bullet-list-btn"
      >
        <List className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered List"
        testId="ordered-list-btn"
      >
        <ListOrdered className="w-4 h-4" />
      </TBtn>
      <Sep />
      <TBtn
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })}
        title="Align Left"
        testId="align-left-btn"
      >
        <AlignLeft className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })}
        title="Align Center"
        testId="align-center-btn"
      >
        <AlignCenter className="w-4 h-4" />
      </TBtn>
      <TBtn
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        active={editor.isActive({ textAlign: 'right' })}
        title="Align Right"
        testId="align-right-btn"
      >
        <AlignRight className="w-4 h-4" />
      </TBtn>
      <Sep />

      {/* Text Color Picker */}
      <div className="relative" ref={colorRef}>
        <TBtn
          onClick={() => { setColorOpen(!colorOpen); setHighlightOpen(false); }}
          title="Text Color"
          testId="text-color-picker"
        >
          <Type className="w-4 h-4" />
        </TBtn>
        {colorOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-2 z-50 grid grid-cols-5 gap-1.5 min-w-[140px]">
            {TEXT_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { editor.chain().focus().setColor(c).run(); setColorOpen(false); }}
                className="w-6 h-6 rounded-full border border-slate-200 hover:scale-125 transition-transform"
                style={{ backgroundColor: c }}
                data-testid={`text-color-${c.slice(1)}`}
              />
            ))}
            <button
              onClick={() => { editor.chain().focus().unsetColor().run(); setColorOpen(false); }}
              className="col-span-5 text-xs text-slate-500 hover:text-slate-700 mt-1"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {/* Highlight Color Picker */}
      <div className="relative" ref={highlightRef}>
        <TBtn
          onClick={() => { setHighlightOpen(!highlightOpen); setColorOpen(false); }}
          title="Highlight"
          testId="highlight-picker"
        >
          <Highlighter className="w-4 h-4" />
        </TBtn>
        {highlightOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-2 z-50 grid grid-cols-5 gap-1.5 min-w-[140px]">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { editor.chain().focus().toggleHighlight({ color: c }).run(); setHighlightOpen(false); }}
                className="w-6 h-6 rounded-full border border-slate-200 hover:scale-125 transition-transform"
                style={{ backgroundColor: c }}
                data-testid={`highlight-${c.slice(1)}`}
              />
            ))}
            <button
              onClick={() => { editor.chain().focus().unsetHighlight().run(); setHighlightOpen(false); }}
              className="col-span-5 text-xs text-slate-500 hover:text-slate-700 mt-1"
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeNotePage;
